import { memo } from "react";
import XResizableTextarea from "../common/XResizableTextarea";
import useDemandStudioComment from "../../hooks/useDemandStudioComment";
import XButton from "../common/XButton";
import { cn } from "../../utils/classNames";
import { textareaFieldStyleClasses } from "../common/formInputClasses";
import {
  textEditorSectionClasses,
  textEditorSectionAltClasses,
} from "./DemandStudioSectionEditor";
import { useDemandStudioContext } from "../../contexts/DemandStudioContext";

const sectionTitleWrapClasses = "flex flex-col gap-0.5";
const sectionOrderClasses =
  "text-[calc(var(--spacing)*2.75)] font-bold uppercase tracking-[calc(var(--spacing)*0.225)] text-subtle";
const sectionTitleClasses = "text-lg leading-6 font-bold text-content";
const editSectionBtnClasses =
  "text-primary font-semibold no-underline hover:text-primary-hover text-sm!";

const regenerateButtonClasses = `
  h-10! py-0!
  rounded! text-sm!
  border-border! text-border!
  hover:not-disabled:border-primary! hover:not-disabled:text-primary!
  hover:not-disabled:bg-primary/[0.08]!
`;

const regenerateButtonActiveClasses = 'border-primary! text-primary!';

const inlineSpinnerClasses = "inline-block align-[-0.125em] animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full mr-2 text-primary";

const DemandStudioCommentCard = memo(({ section, index }) => {

  const {
    updateSectionComment,
    handleRegenerateSection,
    handleEditSection,
    handleCardFocus,
    isLoading,
    getSectionUiState,
    focusedSectionId,
  } = useDemandStudioContext();

  const {
    sectionNumber,
    isAlternate,
    label,
    name,
    value,
    uiState,
    isFocused,
    onChange,
    onRegenerate,
    onEditSection,
    onCardFocus,
  } = useDemandStudioComment({
    section,
    index,
    updateSectionComment,
    handleRegenerateSection,
    handleEditSection,
    handleCardFocus,
    getSectionUiState,
    focusedSectionId,
  });

  const cardStateClasses = cn(
    isFocused && "bg-primary-surface! shadow-[0_4px_4px_rgba(0,0,0,0.25)]",
    uiState === "success" && (isFocused ? "border-4! border-success!" : "border-2! border-success!"),
    uiState === "failed" && (isFocused ? "border-4! border-danger!" : "border-2! border-danger!"),
    uiState !== "success" && uiState !== "failed" && isFocused && "border-4! border-primary-tint!",
  );
  const alternateClass = isAlternate ? textEditorSectionAltClasses : "";

  return (
    <div className={`${textEditorSectionClasses} ${alternateClass} ${cardStateClasses}`}>
      <div className="flex items-center justify-between mb-2">
        <div className={sectionTitleWrapClasses}>
          <span className={sectionOrderClasses}>Section {sectionNumber}</span>
          <span className={sectionTitleClasses}>{label}</span>
        </div>
        <XButton
          variant="link"
          className={editSectionBtnClasses}
          onClick={onEditSection}
        >
          <i className="fa fa-pencil mr-1!" />
          Edit Section
        </XButton>
      </div>
      <XResizableTextarea
        name={name}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={onCardFocus}
        placeholder="Describe what to change in this section"
        className={textareaFieldStyleClasses}
        minHeight="5rem"
      />
      <div className="mt-2 flex justify-end">
        {uiState === "loading" ? (
          <div className="flex items-center">
            <div className={inlineSpinnerClasses} role="status" />
            <span>Regenerating...</span>
          </div>
        ) : (
          <XButton
            variant="outline"
            className={cn(regenerateButtonClasses, isFocused && regenerateButtonActiveClasses)}
            onClick={onRegenerate}
            disabled={isLoading}
          >
            <i className="fa fa-refresh" />
            Regenerate Section
          </XButton>
        )}
      </div>
    </div>
  );
});

DemandStudioCommentCard.displayName = "DemandStudioCommentCard";

export default DemandStudioCommentCard;
